import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models';

/**
 * Base HTTP Service for IH API
 * Provides common HTTP methods with unified error handling and response parsing
 */
@Injectable({
  providedIn: 'root'
})
export class BaseHttpService {
  protected http = inject(HttpClient);
  protected readonly apiUrl = environment.apiUrl;

  /**
   * GET request with ApiResponse wrapper
   */
  protected get<T>(endpoint: string): Observable<T> {
    return this.http.get<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, {
      headers: this.getNoCacheHeaders()
    }).pipe(
        map(response => this.handleResponse(response)),
        catchError(this.handleError)
      );
  }

  /**
   * POST request with ApiResponse wrapper
   */
  protected post<T>(endpoint: string, body: any): Observable<T> {
    return this.http.post<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, body, {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.handleResponse(response)),
      catchError(this.handleError)
    );
  }

  /**
   * PUT request with ApiResponse wrapper
   */
  protected put<T>(endpoint: string, body: any): Observable<T> {
    return this.http.put<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, body, {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.handleResponse(response)),
      catchError(this.handleError)
    );
  }

  /**
   * DELETE request with ApiResponse wrapper
   */
  protected delete<T>(endpoint: string): Observable<T> {
    return this.http.delete<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`)
      .pipe(
        map(response => this.handleResponse(response)),
        catchError(this.handleError)
      );
  }

  /**
   * GET request for raw response (no ApiResponse wrapper)
   */
  protected getRaw<T>(endpoint: string): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/${endpoint}`, {
      headers: this.getNoCacheHeaders()
    }).pipe(catchError(this.handleError));
  }

  /**
   * POST request for raw response (no ApiResponse wrapper)
   */
  protected postRaw<T>(endpoint: string, body: any): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}/${endpoint}`, body, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  /**
   * Handle API response and extract data
   */
  private handleResponse<T>(response: ApiResponse<T>): T {
    if (response.status === 'success') {
      return response.data as T;
    } else {
      throw new Error(response.message || 'API request failed');
    }
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse | Error): Observable<never> {
    // Plain JS Error thrown by handleResponse (app-level non-success, HTTP 200 body).
    // Just re-throw as-is — no "HTTP Error:" prefix, no extra wrapping.
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    let errorMessage = 'An error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status === 0) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (error.status === 404) {
        errorMessage = 'Resource not found.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else {
        errorMessage = `Server returned code ${error.status}: ${error.message}`;
      }
    }

    console.error('HTTP Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Get HTTP headers
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });
  }

  /**
   * Get HTTP headers with cache-busting directives
   * Used for GET requests to prevent browser/WebView caching
   */
  private getNoCacheHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    });
  }
}
